import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Modal from 'components/atoms/Modal'
import { SkeletonBlock, SkeletonLine } from 'components/Skeleton'
import SuperAdminsTable from 'features/organisation/SuperAdminsTable'
import AddSuperAdminModal from 'features/organisation/AddSuperAdminModal'
import ApproversTable from 'features/organisation/ApproversTable'
import EditApproversModal from 'features/organisation/EditApproversModal'
import organisationService from 'services/organisationService'
import { hideEmail } from 'utils/user'
import { PRIVATE_PATHS } from 'utils/paths'
import { ACCENT, ACCENT_HOVER, BORDER, INK, MUTED } from 'features/actions/workspace/ui'

const initialState = {
  loading: true,
  forbidden: false,
  notFound: false,
  loadError: false,
  organisation: null,
  superAdmins: [],
  invitations: [],
}

const OrganisationPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const tab = new URLSearchParams(location.search).get('tab') === 'approvers' ? 'approvers' : 'admins'
  const setTab = next => {
    const params = new URLSearchParams(location.search)
    if (next === 'approvers') params.set('tab', 'approvers')
    else params.delete('tab')
    const search = params.toString()
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' })
  }
  const [approvers, setApprovers] = useState({ loading: false, loaded: false, error: false, domains: [], sharepoint: [] })
  const [colleagues, setColleagues] = useState([])
  const [editTarget, setEditTarget] = useState(null)
  const [state, setState] = useState(initialState)
  const [addOpen, setAddOpen] = useState(false)
  const [removeTarget, setRemoveTarget] = useState(null)
  const [removeError, setRemoveError] = useState('')
  const [removing, setRemoving] = useState(false)
  const [withdrawingId, setWithdrawingId] = useState(null)

  const load = useCallback(() => {
    setState(s => ({ ...s, loading: true }))
    organisationService.getSuperAdmins()
      .then(res => {
        const data = res?.data || {}
        setState({
          loading: false,
          forbidden: false,
          notFound: false,
          loadError: false,
          organisation: data.organisation || null,
          superAdmins: data.superAdmins || [],
          invitations: data.invitations || [],
        })
      })
      .catch(err => {
        const status = err?.response?.status
        const known = status === 403 || status === 404
        setState({ ...initialState, loading: false, forbidden: status === 403, notFound: status === 404, loadError: !known })
      })
  }, [])

  useEffect(() => { load() }, [load])

  const loadApprovers = useCallback(() => {
    setApprovers(a => ({ ...a, loading: !a.loaded, error: false }))
    organisationService.getApprovers()
      .then(res => {
        const data = res?.data || {}
        setApprovers({ loading: false, loaded: true, error: false, domains: data.domains || [], sharepoint: data.sharepoint || [] })
      })
      .catch(() => setApprovers(a => ({ ...a, loading: false, error: true })))
  }, [])

  useEffect(() => {
    if (tab === 'approvers' && !approvers.loaded && !approvers.loading && !approvers.error) loadApprovers()
  }, [tab, approvers.loaded, approvers.loading, approvers.error, loadApprovers])

  useEffect(() => {
    if (!editTarget || colleagues.length > 0) return
    organisationService.colleagues('')
      .then(res => setColleagues(res?.data?.data || []))
      .catch(() => setColleagues([]))
  }, [editTarget, colleagues.length])

  const handleAdded = (status, name) => {
    setAddOpen(false)
    toast.success(status === 'appointed'
      ? `${name} is now a Super Admin.`
      : "Invitation sent. They'll become a Super Admin when they create their account.")
    load()
  }

  const requestRemove = row => {
    setRemoveError('')
    setRemoveTarget(row)
  }

  const confirmRemove = async () => {
    if (!removeTarget) return
    setRemoving(true)
    setRemoveError('')
    try {
      await organisationService.removeSuperAdmin(removeTarget.userId)
      setRemoveTarget(null)
      toast.success(`${removeTarget.name} is no longer a Super Admin.`)
      load()
    } catch (err) {
      setRemoveError(err?.response?.data?.message || 'We could not remove this Super Admin. Please try again.')
    } finally {
      setRemoving(false)
    }
  }

  const withdraw = async invitation => {
    setWithdrawingId(invitation.id)
    try {
      await organisationService.withdrawInvitation(invitation.id)
      toast.success('Invitation withdrawn.')
      load()
    } catch {
      toast.error('We could not withdraw this invitation. Please try again.')
    } finally {
      setWithdrawingId(null)
    }
  }

  if (!state.loading && (state.forbidden || state.notFound)) {
    return (
      <Page>
        <Blocked>
          <BlockedTitle>Only Super Admins can open this page.</BlockedTitle>
          <Link to={PRIVATE_PATHS.profileSettings}>Back to your profile</Link>
        </Blocked>
      </Page>
    )
  }

  if (!state.loading && state.loadError) {
    return (
      <Page>
        <Blocked>
          <BlockedTitle>We couldn&apos;t load your organisation. Please try again.</BlockedTitle>
          <TryAgainButton type='button' onClick={load}>Try again</TryAgainButton>
        </Blocked>
      </Page>
    )
  }

  return (
    <Page>
      <Head>
        <Title data-tour-id='organisation.heading'>Your organisation</Title>
        {state.loading ? (
          <SkeletonLine $w='50%' $h={16} />
        ) : (
          <Subheading>
            {state.organisation?.name}
            {state.organisation?.domains?.length ? ` · ${state.organisation.domains.join(', ')}` : ''}
          </Subheading>
        )}
        <Intro>
          Super Admins look after {state.organisation?.name || 'your organisation'} on Building Passport. They decide
          who else can open locked documents, and who else is a Super Admin.
        </Intro>
      </Head>

      <Tabs role='tablist'>
        <Tab type='button' role='tab' aria-selected={tab === 'admins'} $on={tab === 'admins'} onClick={() => setTab('admins')}>Super Admins</Tab>
        <Tab type='button' role='tab' data-tour-id='organisation.approvers-tab' aria-selected={tab === 'approvers'} $on={tab === 'approvers'} onClick={() => setTab('approvers')}>Approvers</Tab>
      </Tabs>

      {tab === 'approvers' ? (
        <ApproversPanel>
          <PanelIntro>
            An approver is a person who decides whether someone can open a locked document. By default your Super
            Admins decide, shown as &quot;Super Admins (default)&quot;. Name other people here if you would rather
            they handled requests for a domain or a SharePoint connection.
          </PanelIntro>
          {approvers.loading || (!approvers.loaded && !approvers.error) ? (
            <SkeletonWrap aria-busy='true' aria-label='Loading approvers'>
              <SkeletonLine $h={40} $w='50%' />
              <SkeletonBlock $h={64} />
              <SkeletonBlock $h={64} />
              <SkeletonBlock $h={64} />
            </SkeletonWrap>
          ) : approvers.error ? (
            <Blocked>
              <BlockedTitle>We couldn&apos;t load the approvers. Please try again.</BlockedTitle>
              <TryAgainButton type='button' onClick={loadApprovers}>Try again</TryAgainButton>
            </Blocked>
          ) : (
            <div>
              <ApproversTable domains={approvers.domains} sharepoint={approvers.sharepoint} onEdit={setEditTarget} />
            </div>
          )}
          <EditApproversModal
            target={editTarget}
            organisationDomains={state.organisation?.domains || []}
            colleagues={colleagues}
            onClose={() => setEditTarget(null)}
            onChanged={loadApprovers}
          />
        </ApproversPanel>
      ) : state.loading ? (
        <SkeletonWrap aria-busy='true' aria-label='Loading'>
          <SkeletonLine $h={14} $w='60%' />
          <SkeletonLine $h={14} $w='40%' />
          <SkeletonLine $h={14} $w='80%' />
          <SkeletonBlock $h={180} />
        </SkeletonWrap>
      ) : (
        <>
          <Toolbar>
            <AddButton type='button' data-tour-id='organisation.add-button' onClick={() => setAddOpen(true)}>Add Super Admin</AddButton>
          </Toolbar>

          <div data-tour-id='organisation.table'>
            <SuperAdminsTable rows={state.superAdmins} onRemove={requestRemove} />
          </div>

          <Waiting>
            <WaitingTitle>Waiting to join</WaitingTitle>
            {state.invitations.length === 0 ? (
              <WaitingEmpty>Nobody is waiting to join as a Super Admin.</WaitingEmpty>
            ) : (
              <WaitingList>
                {state.invitations.map(invitation => (
                  <WaitingRow key={invitation.id}>
                    <WaitingInfo>
                      <WaitingEmail>{hideEmail(invitation.email || null)}</WaitingEmail>
                      <WaitingMeta>Invited by {invitation.invitedBy || 'a Super Admin'}</WaitingMeta>
                    </WaitingInfo>
                    <WithdrawButton type='button' disabled={withdrawingId === invitation.id} onClick={() => withdraw(invitation)}>
                      Withdraw
                    </WithdrawButton>
                  </WaitingRow>
                ))}
              </WaitingList>
            )}
          </Waiting>
        </>
      )}

      <AddSuperAdminModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        domains={state.organisation?.domains || []}
        onAdded={handleAdded}
      />

      <Modal
        open={!!removeTarget}
        onClose={() => { if (!removing) setRemoveTarget(null) }}
        title='Remove Super Admin'
        withCancel
        cancelButtonText='Cancel'
        onCancel={() => setRemoveTarget(null)}
        withSubmit
        submitButtonText='Remove'
        disabled={removing}
        onSubmit={confirmRemove}
      >
        <ConfirmText>
          Remove {removeTarget?.name} as a Super Admin? They keep their access to buildings but can no longer decide
          access requests or manage Super Admins.
        </ConfirmText>
        {removeError && <ErrorText>{removeError}</ErrorText>}
      </Modal>
    </Page>
  )
}

export default OrganisationPage

const Page = styled.div`
  max-width: 1040px;
  margin: 0 auto;
  padding: 24px 16px 64px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const Head = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Title = styled.h1`
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  color: ${INK};
`

const Subheading = styled.div`
  font-size: 15px;
  color: ${MUTED};
`

const Intro = styled.p`
  margin: 8px 0 0;
  max-width: 68ch;
  font-size: 15px;
  line-height: 1.55;
  color: ${MUTED};
`

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${BORDER};
`

const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 14px;
  border: none;
  border-bottom: 3px solid ${({ $on }) => ($on ? ACCENT : 'transparent')};
  background: none;
  font: inherit;
  font-size: 15px;
  font-weight: ${({ $on }) => ($on ? 700 : 600)};
  color: ${({ $on }) => ($on ? INK : MUTED)};
  cursor: pointer;
`

const ApproversPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const PanelIntro = styled.p`
  margin: 0;
  max-width: 68ch;
  font-size: 15px;
  line-height: 1.55;
  color: ${MUTED};
`

const SkeletonWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
`

const AddButton = styled.button`
  min-height: 44px;
  padding: 0 18px;
  border: 1px solid ${ACCENT};
  border-radius: 8px;
  background: ${ACCENT};
  color: #fff;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;

  &:hover { background: ${ACCENT_HOVER}; border-color: ${ACCENT_HOVER}; }
`

const Waiting = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid ${BORDER};
`

const WaitingTitle = styled.h2`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${INK};
`

const WaitingEmpty = styled.p`
  margin: 0;
  font-size: 15px;
  color: ${MUTED};
`

const WaitingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const WaitingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`

const WaitingInfo = styled.div`
  display: flex;
  flex-direction: column;
`

const WaitingEmail = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
`

const WaitingMeta = styled.div`
  font-size: 14px;
  color: ${MUTED};
`

const WithdrawButton = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  background: #fff;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:disabled { cursor: not-allowed; color: #98a2b3; }
`

const ConfirmText = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: ${INK};
`

const ErrorText = styled.p`
  margin: 10px 0 0;
  font-size: 14px;
  color: #b00020;
`

const Blocked = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 40px 0;
`

const BlockedTitle = styled.h1`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: ${INK};
`

const TryAgainButton = styled.button`
  align-self: flex-start;
  min-height: 44px;
  padding: 0 18px;
  border: 1px solid ${ACCENT};
  border-radius: 8px;
  background: ${ACCENT};
  color: #fff;
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;

  &:hover { background: ${ACCENT_HOVER}; border-color: ${ACCENT_HOVER}; }
`
